/**
 * The site's public origin — the single source of truth for every SEO signal.
 *
 * Canonical tags, og:url, the sitemap, robots.txt and the schema.org graph all derive from
 * this. They must agree: if a page says its canonical lives on another domain, Google indexes
 * THAT domain and drops this one.
 *
 * That is exactly what happened. This value was duplicated across five files, each defaulting
 * to https://www.meritlens.com, and NEXT_PUBLIC_SITE_URL was never set in .env or injected by
 * any build script — so production served meritlens.ai while telling Google the real site was
 * meritlens.com, a host that does not answer on HTTPS at all. Google honoured the canonical,
 * failed to fetch it, and left meritlens.ai out of the index.
 *
 * The default is now the live origin, so a missing env var degrades to "correct" rather than
 * "invisible". NEXT_PUBLIC_* is inlined at BUILD time, so setting it only at runtime has no
 * effect — it must be present when `next build` runs.
 */
const DEFAULT_SITE_URL = 'https://meritlens.ai'

/** Trailing slashes produce `https://meritlens.ai//about`, which Google reads as a separate URL. */
function normalise(url: string): string {
  return url.trim().replace(/\/+$/, '')
}

export const SITE_URL: string = normalise(
  process.env.NEXT_PUBLIC_SITE_URL || DEFAULT_SITE_URL,
)

/** Absolute URL for a path, for canonical/og tags and the sitemap. */
export function absoluteUrl(path = ''): string {
  if (!path) return SITE_URL
  return `${SITE_URL}/${path.replace(/^\/+/, '')}`
}
